import { stripCitationMarkersWithMap } from '@shared/citation-markers'
import type { GroundingSupport } from '@shared/grounded-chat-types'

export type GroundingIntegrityIssue =
  | 'missing_supports'
  | 'no_substantive_text'
  | 'invalid_segment_bounds'
  | 'segment_text_mismatch'
  | 'invalid_source_index'
  | 'incomplete_coverage'

export type UncoveredGroundingRange = {
  startIndex: number
  endIndex: number
  text: string
}

export function isGroundingEvidenceUsable(result: {
  issues: ReadonlyArray<GroundingIntegrityIssue>
}) {
  return result.issues.every((issue) => issue === 'incomplete_coverage')
}

function clampIndex(value: number, length: number) {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(length, Math.trunc(value)))
}

export function canonicalizeGroundingSupports(
  rawText: string,
  groundingSupports: ReadonlyArray<GroundingSupport>,
) {
  const { cleanedText, rawToCleanIndex } = stripCitationMarkersWithMap(rawText)
  const supports = groundingSupports.map((support) => {
    const rawStart = clampIndex(support.segment.startIndex, rawText.length)
    const rawEnd = clampIndex(support.segment.endIndex, rawText.length)
    const startIndex = rawToCleanIndex[rawStart]
    const endIndex = rawToCleanIndex[rawEnd]

    return {
      segment: {
        startIndex,
        endIndex,
        text: cleanedText.slice(startIndex, endIndex),
      },
      groundingChunkIndices: [...support.groundingChunkIndices],
    }
  })

  return { cleanedText, groundingSupports: supports }
}

export function normalizeGroundingSupportsForDisplay(
  sourceText: string,
  groundingSupports: ReadonlyArray<GroundingSupport>,
) {
  const { cleanedText } = stripCitationMarkersWithMap(sourceText)
  const usesCanonicalOffsets = groundingSupports.every(({ segment }) => {
    return (
      Number.isInteger(segment.startIndex) &&
      Number.isInteger(segment.endIndex) &&
      segment.startIndex >= 0 &&
      segment.endIndex > segment.startIndex &&
      segment.endIndex <= cleanedText.length &&
      cleanedText.slice(segment.startIndex, segment.endIndex) === segment.text
    )
  })
  if (usesCanonicalOffsets) {
    return groundingSupports.map((support) => ({
      segment: { ...support.segment },
      groundingChunkIndices: [...support.groundingChunkIndices],
    }))
  }
  return canonicalizeGroundingSupports(sourceText, groundingSupports)
    .groundingSupports
}

function isSubstantive(character: string) {
  return /[\p{L}\p{N}]/u.test(character)
}

function findUncoveredRanges(
  text: string,
  supports: ReadonlyArray<GroundingSupport>,
) {
  const ranges: Array<UncoveredGroundingRange> = []
  let rangeStart: number | null = null

  for (let index = 0; index < text.length; index += 1) {
    const substantive = isSubstantive(text[index])
    const covered = supports.some(
      ({ segment }) => index >= segment.startIndex && index < segment.endIndex,
    )

    if (substantive && !covered && rangeStart === null) {
      rangeStart = index
    } else if ((!substantive || covered) && rangeStart !== null) {
      ranges.push({
        startIndex: rangeStart,
        endIndex: index,
        text: text.slice(rangeStart, index),
      })
      rangeStart = null
    }
  }

  if (rangeStart !== null) {
    ranges.push({
      startIndex: rangeStart,
      endIndex: text.length,
      text: text.slice(rangeStart),
    })
  }

  return ranges
}

export function validateGroundingCoverage(
  rawText: string,
  groundingSupports: ReadonlyArray<GroundingSupport>,
  sourceCount: number,
) {
  const issues = new Set<GroundingIntegrityIssue>()
  if (groundingSupports.length === 0) issues.add('missing_supports')

  for (const support of groundingSupports) {
    const { startIndex, endIndex, text } = support.segment
    const validBounds =
      Number.isInteger(startIndex) &&
      Number.isInteger(endIndex) &&
      startIndex >= 0 &&
      endIndex > startIndex &&
      endIndex <= rawText.length

    if (!validBounds) {
      issues.add('invalid_segment_bounds')
    } else if (rawText.slice(startIndex, endIndex) !== text) {
      issues.add('segment_text_mismatch')
    }

    if (
      support.groundingChunkIndices.length === 0 ||
      support.groundingChunkIndices.some(
        (index) =>
          !Number.isInteger(index) || index < 0 || index >= sourceCount,
      )
    ) {
      issues.add('invalid_source_index')
    }
  }

  const canonical = canonicalizeGroundingSupports(rawText, groundingSupports)
  const hasSubstantiveText = [...canonical.cleanedText].some(isSubstantive)
  if (!hasSubstantiveText) issues.add('no_substantive_text')

  const uncoveredRanges = findUncoveredRanges(
    canonical.cleanedText,
    canonical.groundingSupports,
  )
  if (uncoveredRanges.length > 0) issues.add('incomplete_coverage')

  return {
    ...canonical,
    valid: issues.size === 0,
    issues: [...issues],
    uncoveredRanges,
  }
}
